'use client';

import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'motion/react';

const EASE = [0.22, 1, 0.36, 1] as const;

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

interface InViewProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  y?: number;
  x?: number;
  scale?: number;
  once?: boolean;
}

export function InView({
  children,
  className,
  delay = 0,
  duration = 0.7,
  y = 28,
  x = 0,
  scale = 1,
  once = true,
}: InViewProps) {
  const reduced = useReducedMotion();
  const mounted = useMounted();

  /* Before mount: render with the same initial CSS state so server/client match */
  if (!mounted || reduced) {
    return (
      <div
        className={className}
        style={mounted || reduced ? undefined : { opacity: 0, transform: `translate(${x}px, ${y}px) scale(${scale})` }}
      >
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y, x, scale }}
      whileInView={{ opacity: 1, y: 0, x: 0, scale: 1 }}
      viewport={{ once, margin: '-80px' }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/* ── Stagger container ───────────────────────────────────────────────────── */
interface StaggerProps {
  children: ReactNode;
  className?: string;
  stagger?: number;
  delayChildren?: number;
  as?: 'div' | 'ul';
}

export function Stagger({ children, className, stagger = 0.1, delayChildren = 0, as = 'div' }: StaggerProps) {
  const reduced = useReducedMotion();
  const mounted = useMounted();

  if (!mounted || reduced) {
    return as === 'ul' ? <ul className={className}>{children}</ul> : <div className={className}>{children}</div>;
  }

  const props = {
    className,
    initial: 'hidden',
    whileInView: 'visible',
    viewport: { once: true, margin: '-80px' },
    variants: {
      hidden: {},
      visible: { transition: { staggerChildren: stagger, delayChildren } },
    },
  };

  return as === 'ul' ? (
    <motion.ul {...props}>{children}</motion.ul>
  ) : (
    <motion.div
      {...props}
    >
      {children}
    </motion.div>
  );
}

/* ── Single stagger child ────────────────────────────────────────────────── */
export function StaggerChild({
  children,
  className,
  y = 24,
  duration = 0.65,
  as = 'div',
}: {
  children: ReactNode;
  className?: string;
  y?: number;
  duration?: number;
  as?: 'div' | 'li';
}) {
  const props = {
    className,
    variants: {
      hidden: { opacity: 0, y },
      visible: { opacity: 1, y: 0, transition: { duration, ease: EASE } },
    },
  };

  return as === 'li' ? (
    <motion.li {...props}>{children}</motion.li>
  ) : (
    <motion.div
      {...props}
    >
      {children}
    </motion.div>
  );
}

